import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Globe, Facebook, Linkedin, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import logo from '../assets/logo.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = (key) => getTranslation(key, language);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t('home'), href: '#home' },
    { label: t('services'), href: '#services' },
    { label: t('packages'), href: '#packages' },
    { label: t('about'), href: '#about' },
    { label: t('contact'), href: '#contact' },
  ];

  return (
    <header
      className={`fixed top-0 ${language === 'ar' ? 'right-0 left-0' : 'left-0 right-0'} z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-lg py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="flex items-center transition-transform hover:scale-105 duration-300">
            <img
              src={logo}
              alt="Zuhoor Nizwa Travel & Tourism"
              className="h-16 sm:h-20 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2 rtl:space-x-reverse">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:bg-[#1E7B8C]/10 hover:text-[#1E7B8C] ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Contact Info, Social Media, Language Toggle & CTA */}
          <div className="hidden lg:flex items-center space-x-4 rtl:space-x-reverse">
            {/* Social Media Icons */}
            <div className="flex items-center space-x-2 rtl:space-x-reverse border-r border-gray-300/30 pr-4 rtl:pr-0 rtl:pl-4 rtl:border-l rtl:border-r-0">
              <a
                href="https://www.facebook.com/ZuhoorNizwaTravels/"
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-full transition-all duration-300 hover:bg-[#1877F2] hover:text-white ${
                  isScrolled ? 'text-gray-600 hover:scale-110' : 'text-white/90 hover:scale-110'
                }`}
                title="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/zuhoor-nizwa-travels-and-tourism"
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-full transition-all duration-300 hover:bg-[#0A66C2] hover:text-white ${
                  isScrolled ? 'text-gray-600 hover:scale-110' : 'text-white/90 hover:scale-110'
                }`}
                title="LinkedIn"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href="https://www.instagram.com/zuhoor_nizwa_travels"
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-full transition-all duration-300 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] hover:text-white ${
                  isScrolled ? 'text-gray-600 hover:scale-110' : 'text-white/90 hover:scale-110'
                }`}
                title="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-2 rtl:space-x-reverse px-3 py-2 rounded-lg font-medium transition-all duration-300 hover:bg-[#1E7B8C]/10 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              title={language === 'en' ? 'التبديل إلى العربية' : 'Switch to English'}
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm font-semibold">{language === 'en' ? 'عربي' : 'EN'}</span>
            </button>
            
            <a
              href="tel:+96891780974"
              className="flex items-center space-x-2 rtl:space-x-reverse text-sm font-medium hover:text-[#1E7B8C] transition-colors duration-300"
            >
              <Phone className="h-4 w-4" />
              <span className={isScrolled ? 'text-gray-700' : 'text-white'}>+968 9178 0974</span>
            </a>
            <Button
              onClick={() => document.getElementById('callback-form').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#F4B41A] hover:bg-[#E5A615] text-white font-semibold px-6 py-2.5 rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
            >
              {t('requestCallBack')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2 rtl:space-x-reverse">
            {/* Mobile Language Toggle */}
            <button
              onClick={toggleLanguage}
              className={`p-2 rounded-lg hover:bg-gray-100/10 transition-colors duration-300 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              aria-label="Toggle language"
            >
              <Globe className="h-5 w-5" />
            </button>
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100/10 transition-colors duration-300"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className={`h-6 w-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
              ) : (
                <Menu className={`h-6 w-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-2xl border-t border-gray-100 animate-in slide-in-from-top-5 duration-300">
            <nav className="flex flex-col p-4 space-y-2">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg font-medium text-gray-700 hover:bg-[#1E7B8C]/10 hover:text-[#1E7B8C] transition-all duration-300"
                >
                  {item.label}
                </a>
              ))}
              <div className="pt-4 space-y-3 border-t border-gray-200">
                <a
                  href="tel:+96891780974"
                  className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 text-gray-700 hover:text-[#1E7B8C] transition-colors duration-300"
                >
                  <Phone className="h-4 w-4" />
                  <span>+968 9178 0974</span>
                </a>
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    document.getElementById('callback-form').scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-[#F4B41A] hover:bg-[#E5A615] text-white font-semibold"
                >
                  {t('requestCallBack')}
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;